import { AlertApi, CurrentLogApi, IAlertLightsData, IAlertStatus, IAlertStatusType, IAppState, IDay, ILog, ILogLighting, IShip, IShipVesselType, IStatus, IStatusType } from "../../Types"
import { lightingTranslationKey } from "../../Components/LogButton/Lighting"
import { IconType, Icons } from "../../Components/Icon"
import { NavigationLightsIconData } from "../../Components/NavigationLightsIcon"
import { UserAdviceLevel, UserAdvicePrimaryAction, UserAdviceRole } from "../../Components/UserAdvice/Params"
import { AlertDeliveryConsumer } from "../../App/AlertDelivery"
import { logger } from "../../App/Logger"
import { FocusViewState } from "../FocusView"
import { UserAdvice, UserAdviceInstance, UserAdviceLocation, UserAdviceState } from "./Advice"
import { OverlayViewDefinition } from "../OverlayView"
import { userAdviceAlertOverlay } from "./AlertView"
import { alertProblemSummary } from "./AlertMessage"
import { AlertDeliveryAction, decideDelivery, swiftDateToEpochMs } from "../../App/AlertDelivery"

export class AlertAdvice extends UserAdvice {
  readonly role = UserAdviceRole.alert;
  active: boolean = false;
  alerts: IStatus[] = [];
  // The alert's day, kept so the dialog can resolve who may clear the alert: that
  // depends on the day's skipper and watch plan (and the ship-local hour), none of
  // which are reachable from the dialog itself.
  day?: IDay;
  vesselType?: IShipVesselType;
  delivery = new AlertDeliveryConsumer();
  alertLevel = {
    [IAlertStatus[IAlertStatus.alarm]]: 1000,
    [IAlertStatus[IAlertStatus.warning]]: 500,
    "default": 10,
  };

  update(app: IAppState, state: FocusViewState, currentLog?: ILog, ship?: IShip) {
    this.active = true;
    this.alerts = [];
    this.vesselType = ship?.vesselType;
    if (!currentLog) {
      this.active = false;
      this.delivery.reset();
      return;
    }
    this.day = state.days?.find((d) => d.id == currentLog.dayId);
    const alertStatuses = UserAdvice.status(app.status?.nmea?.liveAlerts ?? state.statuses, IStatusType.alert, currentLog.dayId).filter((s) => s.endLogId == undefined).toSorted((a, b) => b.id - a.id);
    if (alertStatuses.length < 0) {
      this.active = false;
      return;
    }
    this.alerts = AlertAdvice.byEpisode(alertStatuses);
    this.deliver(app);
  }

  // Collapse the open alert statuses down to one per *episode* (#1269).
  //
  // The server recreates an alert's Status row on every transition and closes the
  // previous one, so a single episode's row id rotates over its life. A device that
  // applies the create but misses or reorders the matching close holds several open
  // rows for one episode -- and one advice button is rendered per open row, so the
  // crew sees the same alert (e.g. "Lost Position") several times over.
  //
  // Grouping by the envelope's episode id (rather than by name) keeps genuinely
  // concurrent episodes -- several messages, several MOB beacons -- as separate
  // advices. Rows written before episode ids existed carry none, so they fall back
  // to the alert name, which is exactly right: every alert was a singleton then.
  // `statuses` arrives newest-first, so the first of each group is the current row.
  private static byEpisode(statuses: IStatus[]): IStatus[] {
    const seen = new Set<string>();
    return statuses.filter((s) => {
      const key = AlertAdvice.episodeKey(s);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  static episodeKey(alert: IStatus): string {
    try {
      const episode = JSON.parse(alert.data || "{}")?._episode;
      if (episode && typeof episode.id == "string" && episode.id.length > 0) {
        return episode.id;
      }
    } catch (e) {
      logger.debug("AlertAdvice episodeKey", e);
    }
    return alert.name || "";
  }

  static indicators(app: IAppState, alert: IStatus, nowMs = Date.now()): { labels: string[]; icons: { icon: IconType; label: string }[] } {
    const t = app.getT("UserAdviceAlertView");
    const labels: string[] = [];
    const icons: { icon: IconType; label: string }[] = [];
    try {
      const episode = JSON.parse(alert.data || "{}")?._episode;
      const snoozeUntilMs = episode?.snoozeUntil != null ? swiftDateToEpochMs(episode.snoozeUntil) : undefined;
      if (snoozeUntilMs != undefined && nowMs < snoozeUntilMs) {
        labels.push(t("state_snoozed"));
      }
      if (episode?.acknowledgedBy != null) {
        labels.push(t("state_acknowledged"));
      }
    } catch {
      // The delivery decision below has the same safe fallback for legacy rows.
    }
    if (decideDelivery(alert, app.user?.id, nowMs, app.primarySession()) == AlertDeliveryAction.alarm) {
      icons.push({ icon: Icons.Sounding, label: t("state_audible") });
    }
    return { labels, icons };
  }

  // Drive the native delivery bridge (audible alarm + OS notification) from the
  // persisted alert envelope, for the signed-in user only. This is the client
  // mirror of the primary's own delivery so any device — including a plain
  // browser — presents an open alert without a bespoke realtime wire type.
  private deliver(app: IAppState) {
    const t = app.getT("UserAdviceAlertView");
    const describe = (alert: IStatus) => {
      const type = alert.name ? t(`alert_${alert.name}`) : t("title");
      const level = t(`alert_${IAlertStatus[alert.value] || "default"}`);
      let body = level;
      if (alert.name == IAlertStatusType.message) {
        try {
          const message = JSON.parse(alert.data || "{}").message;
          if (typeof message == "string" && message.length > 0) body = message;
        } catch (err) {
          logger.debug("AlertAdvice describe", err);
        }
      }
      return { title: t("title", { name: type, level }), body };
    };
    const now = app.status?.nmea?.liveAlertTime;
    this.delivery.reconcile(this.alerts, app.user?.id, now ? Date.parse(now) : Date.now(), describe, app.primarySession(), (alert) => this.reportReceipt(app, alert));
  }

  // Eagerly (fire-and-forget) tell the primary that this device delivered the
  // alarm locally over the LAN, so the cloud never sends a redundant remote
  // push. The receipt carries the alert's stable episode identity (day + status
  // id); the primary derives the confirming user from the authenticated
  // session. Best-effort with a very small timeout — a lost receipt only risks a
  // (harmless) duplicate push.
  private reportReceipt(app: IAppState, alert: IStatus) {
    if (!alert.name || alert.id < 0) return;
    new AlertApi(app.apiClient)
      .alert.receipt({ params: { alertName: alert.name }, body: { dayId: alert.dayId, statusId: alert.id } })
      .call()
      .catch((err) => logger.debug("AlertAdvice receipt", err));
  }

  level(alert: IStatus): UserAdviceLevel {
    switch (alert.value) {
      case IAlertStatus.alarm:
        return UserAdviceLevel.alarm;
      case IAlertStatus.warning:
        return UserAdviceLevel.warning;
      default:
        return UserAdviceLevel.notice;
    }
  }

  // The icon each alert type shows on its button (#1171). Every open alert used to
  // render the same filled dot, so the whole button width went on spelling its name
  // out -- with a per-type icon the name can be shortened away when the row is tight.
  // `test` and any type without an icon of its own fall back to the generic warning
  // triangle rather than going blank.
  static icon(alert: IStatus): IconType {
    switch (alert.name) {
      case IAlertStatusType.anchorDrift:
        return Icons.AdviceAnchorDrift;
      case IAlertStatusType.depth:
        return Icons.AdviceDepth;
      case IAlertStatusType.lights:
        return Icons.AdviceLights;
      case IAlertStatusType.lostPosition:
        return Icons.AdviceLostPosition;
      case IAlertStatusType.manOverboard:
        return Icons.AdviceManOverboard;
      case IAlertStatusType.message:
        return Icons.AdviceMessage;
      case IAlertStatusType.pressure:
        return Icons.AdvicePressure;
      case IAlertStatusType.wind:
        return Icons.AdviceWind;
      default:
        return Icons.AdviceTest;
    }
  }

  static navigationLights(alert: IStatus): ILogLighting | undefined {
    if (alert.name != IAlertStatusType.lights) {
      return undefined;
    }
    try {
      const recommended = (JSON.parse(alert.data || "{}") as IAlertLightsData).recommended;
      return NavigationLightsIconData.supports(recommended) ? recommended : undefined;
    } catch (err) {
      logger.debug("AlertAdvice navigationLights", err);
      return undefined;
    }
  }

  getInstances(app: IAppState, location: UserAdviceLocation, currentLog?: ILog): UserAdviceInstance[] {
    if (!this.active || !currentLog || location != UserAdviceLocation.log) {
      return [];
    }
    const t = app.getT("UserAdviceAlertView");
    return this.alerts.map((s, i) => {
      const title = t(`alert_${s.name}`);
      const problem = alertProblemSummary(app, s, this.vesselType);
      const liveTime = app.status?.nmea?.liveAlertTime;
      const indicators = AlertAdvice.indicators(app, s, liveTime ? Date.parse(liveTime) : Date.now());
      return new UserAdviceInstance(`alert-${s.id}`, this.alertLevel[IAlertStatus[s.value]] || this.alertLevel["default"], { icon: AlertAdvice.icon(s), navigationLights: AlertAdvice.navigationLights(s), level: this.level(s), title: problem ? `${title} ${problem}` : title, indicators: indicators.labels, indicatorIcons: indicators.icons, type: "alert", primaryAction: AlertAdvice.primaryAction(app, s, this.vesselType) }, this, i, location);
    });
  }

  // @almute's "Lösung 2" (#916): the navigation-lights reminder records the
  // required lighting on a plain click, and its caret opens the dialog with
  // snooze/acknowledge/clear and the full list. Recording needs `.editCurrent`, so
  // the route's own predicate rides along and the button collapses back to the
  // dialog for anyone without it.
  //
  // `recommended` is `nil` exactly in the "ask" case -- a sailing vessel whose
  // engine state is unknown, where the server has nothing to recommend -- so there
  // is nothing to short-cut and the button opens the dialog as before.
  static primaryAction(app: IAppState, alert: IStatus, vesselType?: IShipVesselType): UserAdvicePrimaryAction | undefined {
    if (alert.name != IAlertStatusType.lights) {
      return undefined;
    }
    const recommended = (JSON.parse(alert.data || "{}") as IAlertLightsData).recommended;
    if (recommended == undefined) {
      return undefined;
    }
    const request = new CurrentLogApi(app.apiClient).current.lighting({ params: {}, body: { lighting: recommended } });
    return {
      label: app.getT("UserAdviceAlertView")("record", { lighting: app.t(lightingTranslationKey(recommended, vesselType)) }),
      available: request.available,
      perform: () => {
        try {
          request.call().catch((err) => logger.debug("recordRecommended", err));
        } catch (err) {
          logger.debug("recordRecommended", err);
        }
      },
    };
  }

  overlay(app: IAppState, advice: UserAdviceInstance, state: UserAdviceState): OverlayViewDefinition | undefined {
    const alert = this.alerts[advice.index];
    if (!alert) {
      return undefined;
    }
    return userAdviceAlertOverlay(app, { advice, alert, state, day: this.day, vesselType: this.vesselType });
  }
}
